import type { FC } from 'react';
import React, { useEffect } from 'react';
import { graphql } from 'react-relay';
import { styled } from '@compiled/react';

import { VIEW_PAGE_FAVORITE_BUTTON_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceSuccess,
	ExperienceFailure,
} from '@confluence/experience-tracker/entry-points/component';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd';
import { PageStarButton } from '@confluence/action-buttons/entry-points/pageStar';
import { UNIVERSAL_STAR_BUTTON_SHORTCUT } from '@confluence/shortcuts/entry-points/confluence-shortcuts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';

import { FavouriteButtonPlaceholder } from './FavouriteButtonPlaceholder';
import { FAVOURITE_BUTTON_METRIC } from './perf.config';
import type {
	FavouriteButtonComponentQuery$data,
	FavouriteButtonComponentQuery$variables,
} from './__generated__/FavouriteButtonComponentQuery.graphql';

type FavouriteButtonComponentProps = {
	contentId: string;
	onRender?: () => void;
	isDisabled?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const FavouriteButtonComponentQuery = graphql`
	query FavouriteButtonComponentQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			type
			metadata {
				currentuser {
					favourited {
						isFavourite
					}
				}
			}
		}
		contentOperations: singleContent(id: $contentId) {
			id
			operations {
				operation
				targetType
			}
		}
	}
`;

export const FavouriteButtonComponent: FC<FavouriteButtonComponentProps> = ({
	contentId,
	onRender,
	isDisabled,
}) => {
	const { data, loading, error } = useQueryInterop<
		FavouriteButtonComponentQuery$data,
		FavouriteButtonComponentQuery$variables
	>(FavouriteButtonComponentQuery, {
		variables: {
			contentId,
		},
		fetchPolicy: 'cache-first',
	});

	const contentType = data?.singleContent?.type;

	const hasReadPermission = Boolean(
		data?.contentOperations?.operations?.filter(
			(operation) => operation?.targetType === contentType && operation?.operation === 'read',
		).length,
	);

	useEffect(() => {
		if (onRender && data) {
			onRender();
		}
	});

	if (error) {
		return (
			<>
				<ExperienceFailure name={VIEW_PAGE_FAVORITE_BUTTON_EXPERIENCE} error={error} />
				<ErrorDisplay error={error} />
			</>
		);
	}

	if (loading) {
		return <FavouriteButtonPlaceholder />;
	}

	const isStarred = Boolean(data?.singleContent?.metadata.currentuser?.favourited?.isFavourite);

	return (
		<>
			{hasReadPermission && (
				<ButtonSpacer>
					<PageStarButton
						contentId={contentId}
						isStarred={isStarred}
						shortcut={UNIVERSAL_STAR_BUTTON_SHORTCUT}
						tooltipPosition="bottom"
						analytics={{
							source: 'FavouriteButton',
							attributes: { componentVersion: 'v3', pageType: contentType },
						}}
						isDisabled={isDisabled}
					/>
				</ButtonSpacer>
			)}
			<ExperienceSuccess
				name={VIEW_PAGE_FAVORITE_BUTTON_EXPERIENCE}
				attributes={{
					isFavouritedByCurrentUser: isStarred,
				}}
			/>
			<PageSegmentLoadEnd
				key={contentId}
				metric={FAVOURITE_BUTTON_METRIC}
				customData={{ isVisible: hasReadPermission }}
			/>
		</>
	);
};
